import { useState, type ReactNode } from "react";
import type { CatalogConnector } from "../../../shared/connector-catalog";
import type { ConnectorDTO } from "../../../shared/types";
import { alertDialog } from "../../components/Dialog";
import { BrandIcon } from "../../components/BrandIcon";
import { Switch } from "../../components/Switch";
import { Tabs, useTabParam } from "../../components/Tabs";
import { AgentLineWarning, Badge, Button, ErrorText, Field, inputCls } from "../../components/ui";
import { patch } from "../../lib/api";
import { ACCESS_FILTERS, ConnectorAccessReport } from "./ConnectorAccessReport";
import { AccessLevels, WorkspaceKey } from "./ConnectorKey";
import { InternalPass } from "./ConnectorPass";
import { ToolPermissions } from "./ConnectorPermissions";
import { ConnectorRule, PeoplesLogin, useConnectorAccess } from "./ConnectorWays";

export const MANAGE_TABS = ["setup", "access", "permissions", "advanced"] as const;

/**
 * A connector's Manage pop-up, in four tabs kept in the address with the connector itself, so a link opens it where it
 * was: Setup (the rule, people's own login, the workspace key and its levels), Access (who has it, a report), Tool
 * permissions (what each of its actions needs) and Advanced (the address, the auth, the usage guide).
 */
export function ManageConnector({ connector, entry, me, banner, onSaved, busy, run, error }: {
  connector: ConnectorDTO;
  entry: CatalogConnector | undefined;
  /** The signed-in admin, for "Test my login". */
  me: string | null;
  /** A line about the connector as a whole, above the tabs (off, to be deleted). */
  banner?: ReactNode;
  onSaved: () => Promise<void> | void;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  error: string | null;
}) {
  const [tab, setTab] = useTabParam(MANAGE_TABS);
  const [, setWho] = useTabParam(ACCESS_FILTERS, "who");
  const { access, reload } = useConnectorAccess(connector);
  const saved = async () => {
    await onSaved();
    void reload();
  };
  return (
    <div className="space-y-5">
      <Tabs
        label="Section"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "setup", label: "Setup" },
          { key: "access", label: "Access", count: access ? access.agents.length + access.people.length : null },
          { key: "permissions", label: "Tool permissions" },
          { key: "advanced", label: "Advanced" },
        ]}
      />
      {banner}
      {tab === "setup" && (
        <>
          <ConnectorRule connector={connector} access={access} />
          {connector.credentials === "internal" ? (
            <InternalPass connector={connector} onShowAccess={() => setTab("access")} />
          ) : (
            <>
              <PeoplesLogin
                connector={connector}
                access={access}
                me={me}
                onSaved={saved}
                run={run}
                onShowPeople={() => {
                  setWho("people");
                  setTab("access");
                }}
              />
              <WorkspaceKey connector={connector} entry={entry} access={access} onSaved={saved} busy={busy} run={run} />
            </>
          )}
          <AccessLevels connector={connector} entry={entry} onSaved={saved} />
        </>
      )}
      {tab === "access" && <ConnectorAccessReport connector={connector} access={access} />}
      {tab === "permissions" && <ToolPermissions connector={connector} onSaved={saved} onShowSetup={() => setTab("setup")} />}
      {tab === "advanced" && <AdvancedSettings connector={connector} busy={busy} run={run} onSaved={saved} />}
      <ErrorText error={error} />
    </div>
  );
}

/** The address, the auth config, the usage guide and provenance: what a person rarely touches. */
function AdvancedSettings({ connector, busy, run, onSaved }: { connector: ConnectorDTO; busy: boolean; run: (fn: () => Promise<void>) => Promise<void>; onSaved: () => Promise<void> }) {
  const [advanced, setAdvanced] = useState({
    baseUrl: connector.baseUrl, authConfig: JSON.stringify(connector.authConfig, null, 2), usageGuide: connector.usageGuide,
    sendProvenance: connector.sendProvenance,
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge>{connector.authType}</Badge>
        {typeof connector.authConfig.github_account === "string" && <Badge tone="blue"><BrandIcon brand="github" className="mr-1 size-3" />{connector.authConfig.github_account}</Badge>}
        <span className="font-mono text-xs text-muted">{connector.slug}</span>
      </div>
      <Field label="Base URL">
        <input className={`${inputCls} font-mono`} value={advanced.baseUrl} onChange={(e) => setAdvanced({ ...advanced, baseUrl: e.target.value })} />
      </Field>
      <Field label="Auth config (JSON)">
        <textarea className={`${inputCls} font-mono text-xs`} rows={5} value={advanced.authConfig} onChange={(e) => setAdvanced({ ...advanced, authConfig: e.target.value })} />
      </Field>
      <Field label="Usage guide for agents">
        <textarea className={`${inputCls} font-mono text-xs`} rows={8} value={advanced.usageGuide} onChange={(e) => setAdvanced({ ...advanced, usageGuide: e.target.value })} />
        <AgentLineWarning text={advanced.usageGuide} what="tool" />
      </Field>
      <Switch
        label="Tell this tool which agent and run is calling"
        hint="X-Agent, X-Run and X-Scope, set by the platform; for our own tools."
        checked={advanced.sendProvenance}
        onChange={(sendProvenance) => setAdvanced({ ...advanced, sendProvenance })}
      />
      <Button
        disabled={busy}
        onClick={() =>
          run(async () => {
            const saved = await patch<ConnectorDTO>(`/api/connectors/${connector.id}`, {
              baseUrl: advanced.baseUrl, authConfig: JSON.parse(advanced.authConfig), usageGuide: advanced.usageGuide,
              sendProvenance: advanced.sendProvenance,
            });
            if (saved.secretsCleared) await alertDialog({ title: "Enter the key again", body: "The stored key was cleared because the address or the auth changed. Enter it again under Setup → Workspace key." });
            await onSaved();
          })
        }
      >
        Save advanced settings
      </Button>
      <p className="text-xs text-faint">The connector's on/off is the switch at the top of this window.</p>
    </div>
  );
}
